import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PaperAirplaneIcon,
  LightBulbIcon,
  BuildingOffice2Icon,
  LinkIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { flightsAPI } from '../services/api';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';

type FlightTab = 'routes' | 'tips' | 'airport' | 'booking';

interface TabConfig {
  id: FlightTab;
  name: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  description: string;
}

const TABS: TabConfig[] = [
  { id: 'routes', name: 'Route Research', icon: PaperAirplaneIcon, description: 'Research flight routes between cities' },
  { id: 'tips', name: 'Flight Tips', icon: LightBulbIcon, description: 'Booking tips, baggage, and comfort advice' },
  { id: 'airport', name: 'Airport Guide', icon: BuildingOffice2Icon, description: 'Terminal info, lounges, and amenities' },
  { id: 'booking', name: 'Book Flights', icon: LinkIcon, description: 'Compare prices on booking platforms' },
];

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const CABIN_CLASSES = ['Economy', 'Premium Economy', 'Business', 'First Class'];

export const FlightRouteExplorer: React.FC = () => {
  const { t } = useTranslation(['flights', 'common']);
  const [activeTab, setActiveTab] = useState<FlightTab>('routes');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Route Research tab
  const [routeOrigin, setRouteOrigin] = useState('');
  const [routeDestination, setRouteDestination] = useState('');
  const [routeResult, setRouteResult] = useState<any>(null);

  // Tips tab
  const [tipsOrigin, setTipsOrigin] = useState('');
  const [tipsDestination, setTipsDestination] = useState('');
  const [tipsMonth, setTipsMonth] = useState('');
  const [tipsCabin, setTipsCabin] = useState('');
  const [tipsResult, setTipsResult] = useState<any>(null);

  // Airport tab
  const [airportQuery, setAirportQuery] = useState('');
  const [airportResult, setAirportResult] = useState<any>(null);

  // Booking tab
  const [bookOrigin, setBookOrigin] = useState('');
  const [bookDestination, setBookDestination] = useState('');
  const [bookResult, setBookResult] = useState<any>(null);

  const handleRouteResearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!routeOrigin.trim() || !routeDestination.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await flightsAPI.routeResearch({ origin: routeOrigin.trim(), destination: routeDestination.trim() });
      setRouteResult(result);
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('flights:errors.researchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGetTips = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tipsOrigin.trim() || !tipsDestination.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await flightsAPI.tips({
        origin: tipsOrigin.trim(),
        destination: tipsDestination.trim(),
        travel_month: tipsMonth || undefined,
        cabin_class: tipsCabin || undefined,
      });
      setTipsResult(result);
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('flights:errors.tipsFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleAirportGuide = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!airportQuery.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await flightsAPI.airportGuide({ query: airportQuery.trim() });
      setAirportResult(result);
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('flights:errors.guideFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleBookingLinks = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bookOrigin.trim() || !bookDestination.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await flightsAPI.bookingLinks({ origin: bookOrigin.trim(), destination: bookDestination.trim() });
      setBookResult(result);
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('flights:errors.linksFailed'));
    } finally {
      setLoading(false);
    }
  };

  const renderArray = (arr: any[], fallback = 'N/A') => {
    if (!arr || arr.length === 0) return <span className="text-gray-500 dark:text-gray-400">{fallback}</span>;
    return (
      <ul className="list-disc list-inside space-y-1">
        {arr.map((item, i) => (
          <li key={i} className="text-gray-700 dark:text-gray-300">{typeof item === 'string' ? item : JSON.stringify(item)}</li>
        ))}
      </ul>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <LoadingOverlay isVisible={loading} message="Researching flight information..." subMessage="Our AI is analyzing routes and options" />

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <PaperAirplaneIcon className="h-7 w-7 text-cyan-600" />
          {t('flights:title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Research routes, get flight tips, and find the best deals</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex space-x-4 overflow-x-auto" aria-label="Tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setError(null); }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <Icon className="h-5 w-5" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
          <span className="text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {/* Tab Content */}
      <div className="space-y-6">
        {/* Route Research Tab */}
        {activeTab === 'routes' && (
          <div>
            <form onSubmit={handleRouteResearch} className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('flights:labels.origin')}</label>
                  <input type="text" value={routeOrigin} onChange={(e) => setRouteOrigin(e.target.value)} placeholder="e.g., New York" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('flights:labels.destination')}</label>
                  <input type="text" value={routeDestination} onChange={(e) => setRouteDestination(e.target.value)} placeholder="e.g., Paris" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                </div>
              </div>
              <button type="submit" disabled={loading || !routeOrigin.trim() || !routeDestination.trim()} className="flex items-center gap-2 px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                <MagnifyingGlassIcon className="h-5 w-5" />
                {t('flights:labels.research')}
              </button>
            </form>

            {routeResult && (
              <div className="mt-6 space-y-4">
                {/* Origin Airports */}
                {routeResult.origin_airports && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('flights:sections.originAirports')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {routeResult.origin_airports.map((a: any, i: number) => (
                        <div key={i} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <span className="font-mono font-bold text-cyan-600 dark:text-cyan-400">{a.code}</span>
                          <span className="ml-2 text-gray-700 dark:text-gray-300">{a.name}</span>
                          {a.distance_to_city && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{a.distance_to_city}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Destination Airports */}
                {routeResult.destination_airports && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('flights:sections.destinationAirports')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {routeResult.destination_airports.map((a: any, i: number) => (
                        <div key={i} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <span className="font-mono font-bold text-cyan-600 dark:text-cyan-400">{a.code}</span>
                          <span className="ml-2 text-gray-700 dark:text-gray-300">{a.name}</span>
                          {a.distance_to_city && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{a.distance_to_city}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Direct Flights */}
                {routeResult.direct_flights && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('flights:sections.directFlights')}</h3>
                    <div className="space-y-2">
                      <p className="text-gray-700 dark:text-gray-300">
                        <span className={`inline-block px-2 py-1 rounded text-sm font-medium ${routeResult.direct_flights.available ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'}`}>
                          {routeResult.direct_flights.available ? 'Direct flights available' : 'No direct flights'}
                        </span>
                      </p>
                      {routeResult.direct_flights.airlines && <p className="text-gray-700 dark:text-gray-300"><strong>Airlines:</strong> {routeResult.direct_flights.airlines.join(', ')}</p>}
                      {routeResult.direct_flights.typical_duration && <p className="text-gray-700 dark:text-gray-300"><strong>Duration:</strong> {routeResult.direct_flights.typical_duration}</p>}
                      {routeResult.direct_flights.frequency && <p className="text-gray-700 dark:text-gray-300"><strong>Frequency:</strong> {routeResult.direct_flights.frequency}</p>}
                    </div>
                  </div>
                )}

                {/* Connecting Options */}
                {routeResult.connecting_options && routeResult.connecting_options.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('flights:sections.connectingOptions')}</h3>
                    <div className="space-y-3">
                      {routeResult.connecting_options.map((opt: any, i: number) => (
                        <div key={i} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <p className="font-medium text-gray-900 dark:text-white">{opt.hub}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Airlines: {opt.airlines?.join(', ')}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Total: {opt.total_duration} | Layover: {opt.layover}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Budget Estimate */}
                {routeResult.budget_estimate && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('flights:sections.budgetEstimate')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {routeResult.budget_estimate.economy && (
                        <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                          <p className="text-sm text-gray-500 dark:text-gray-400">Economy</p>
                          <p className="text-lg font-bold text-green-700 dark:text-green-300">{routeResult.budget_estimate.economy}</p>
                        </div>
                      )}
                      {routeResult.budget_estimate.business && (
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                          <p className="text-sm text-gray-500 dark:text-gray-400">Business</p>
                          <p className="text-lg font-bold text-blue-700 dark:text-blue-300">{routeResult.budget_estimate.business}</p>
                        </div>
                      )}
                      {routeResult.budget_estimate.best_booking_window && (
                        <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg text-center">
                          <p className="text-sm text-gray-500 dark:text-gray-400">Best Booking Window</p>
                          <p className="text-sm font-medium text-purple-700 dark:text-purple-300">{routeResult.budget_estimate.best_booking_window}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Seasonal Notes */}
                {routeResult.seasonal_notes && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('flights:sections.seasonalNotes')}</h3>
                    <p className="text-gray-700 dark:text-gray-300">{routeResult.seasonal_notes}</p>
                  </div>
                )}

                {/* Tips */}
                {routeResult.tips && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('flights:sections.tips')}</h3>
                    {renderArray(routeResult.tips)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Flight Tips Tab */}
        {activeTab === 'tips' && (
          <div>
            <form onSubmit={handleGetTips} className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('flights:labels.origin')}</label>
                  <input type="text" value={tipsOrigin} onChange={(e) => setTipsOrigin(e.target.value)} placeholder="e.g., Los Angeles" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('flights:labels.destination')}</label>
                  <input type="text" value={tipsDestination} onChange={(e) => setTipsDestination(e.target.value)} placeholder="e.g., Tokyo" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('flights:labels.travelMonth')} (optional)</label>
                  <select value={tipsMonth} onChange={(e) => setTipsMonth(e.target.value)} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                    <option value="">Any month</option>
                    {MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('flights:labels.cabinClass')} (optional)</label>
                  <select value={tipsCabin} onChange={(e) => setTipsCabin(e.target.value)} className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent">
                    <option value="">Any class</option>
                    {CABIN_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <button type="submit" disabled={loading || !tipsOrigin.trim() || !tipsDestination.trim()} className="flex items-center gap-2 px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                <LightBulbIcon className="h-5 w-5" />
                {t('flights:labels.getTips')}
              </button>
            </form>

            {tipsResult && (
              <div className="mt-6 space-y-4">
                {tipsResult.booking_timing && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('flights:sections.bookingTiming')}</h3>
                    <div className="space-y-2">
                      {Object.entries(tipsResult.booking_timing).map(([key, val]: [string, any]) => (
                        <p key={key} className="text-gray-700 dark:text-gray-300"><strong className="capitalize">{key.replace(/_/g, ' ')}:</strong> {typeof val === 'object' ? JSON.stringify(val) : String(val)}</p>
                      ))}
                    </div>
                  </div>
                )}
                {tipsResult.baggage && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('flights:sections.baggage')}</h3>
                    <div className="space-y-2">
                      {tipsResult.baggage.carry_on && <p className="text-gray-700 dark:text-gray-300"><strong>Carry-on:</strong> {tipsResult.baggage.carry_on}</p>}
                      {tipsResult.baggage.checked && <p className="text-gray-700 dark:text-gray-300"><strong>Checked:</strong> {tipsResult.baggage.checked}</p>}
                      {tipsResult.baggage.tips && renderArray(tipsResult.baggage.tips)}
                    </div>
                  </div>
                )}
                {tipsResult.seat_tips && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('flights:sections.seatTips')}</h3>
                    <div className="space-y-2">
                      {Object.entries(tipsResult.seat_tips).map(([key, val]: [string, any]) => (
                        <p key={key} className="text-gray-700 dark:text-gray-300"><strong className="capitalize">{key.replace(/_/g, ' ')}:</strong> {String(val)}</p>
                      ))}
                    </div>
                  </div>
                )}
                {tipsResult.comfort_tips && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('flights:sections.comfortTips')}</h3>
                    {renderArray(tipsResult.comfort_tips)}
                  </div>
                )}
                {tipsResult.jet_lag && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('flights:sections.jetLag')}</h3>
                    <div className="space-y-2">
                      {tipsResult.jet_lag.time_difference && <p className="text-gray-700 dark:text-gray-300"><strong>Time difference:</strong> {tipsResult.jet_lag.time_difference} ({tipsResult.jet_lag.direction})</p>}
                      {tipsResult.jet_lag.adjustment_tips && renderArray(tipsResult.jet_lag.adjustment_tips)}
                    </div>
                  </div>
                )}
                {tipsResult.loyalty_programs && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('flights:sections.loyaltyPrograms')}</h3>
                    {renderArray(tipsResult.loyalty_programs)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Airport Guide Tab */}
        {activeTab === 'airport' && (
          <div>
            <form onSubmit={handleAirportGuide} className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('flights:labels.airportQuery')}</label>
                <input type="text" value={airportQuery} onChange={(e) => setAirportQuery(e.target.value)} placeholder="e.g., JFK, Heathrow, or Tokyo" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
              </div>
              <button type="submit" disabled={loading || !airportQuery.trim()} className="flex items-center gap-2 px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                <BuildingOffice2Icon className="h-5 w-5" />
                {t('flights:labels.getGuide')}
              </button>
            </form>

            {airportResult && (
              <div className="mt-6 space-y-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">{airportResult.airport_name} <span className="font-mono text-cyan-600 dark:text-cyan-400">({airportResult.airport_code})</span></h3>
                  <p className="text-gray-600 dark:text-gray-400">{airportResult.city}, {airportResult.country}</p>
                </div>

                {airportResult.terminals && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('flights:sections.terminals')}</h3>
                    <div className="space-y-3">
                      {airportResult.terminals.map((term: any, i: number) => (
                        <div key={i} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <p className="font-medium text-gray-900 dark:text-white">{term.name}</p>
                          {term.airlines && <p className="text-sm text-gray-600 dark:text-gray-400">Airlines: {term.airlines.join(', ')}</p>}
                          {term.features && <p className="text-sm text-gray-500 dark:text-gray-400">Features: {term.features.join(', ')}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {airportResult.lounges && airportResult.lounges.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('flights:sections.lounges')}</h3>
                    <div className="space-y-3">
                      {airportResult.lounges.map((l: any, i: number) => (
                        <div key={i} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <p className="font-medium text-gray-900 dark:text-white">{l.name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{l.location} &mdash; {l.access}</p>
                          {l.highlights && <p className="text-sm text-gray-500 dark:text-gray-400">{l.highlights}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {airportResult.ground_transport && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('flights:sections.groundTransport')}</h3>
                    <div className="space-y-3">
                      {airportResult.ground_transport.map((gt: any, i: number) => (
                        <div key={i} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <p className="font-medium text-gray-900 dark:text-white">{gt.type}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{gt.details}</p>
                          {gt.travel_time && <p className="text-sm text-cyan-600 dark:text-cyan-400">{gt.travel_time}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {airportResult.amenities && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('flights:sections.amenities')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {Object.entries(airportResult.amenities).map(([key, val]: [string, any]) => (
                        <div key={key} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{key.replace(/_/g, ' ')}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{String(val)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {airportResult.tips && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('flights:sections.tips')}</h3>
                    {renderArray(airportResult.tips)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Book Flights Tab */}
        {activeTab === 'booking' && (
          <div>
            <form onSubmit={handleBookingLinks} className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('flights:labels.origin')}</label>
                  <input type="text" value={bookOrigin} onChange={(e) => setBookOrigin(e.target.value)} placeholder="e.g., London" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('flights:labels.destination')}</label>
                  <input type="text" value={bookDestination} onChange={(e) => setBookDestination(e.target.value)} placeholder="e.g., Barcelona" className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent" />
                </div>
              </div>
              <button type="submit" disabled={loading || !bookOrigin.trim() || !bookDestination.trim()} className="flex items-center gap-2 px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                <LinkIcon className="h-5 w-5" />
                {t('flights:labels.getLinks')}
              </button>
            </form>

            {bookResult && (
              <div className="mt-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {bookResult.platforms?.map((p: any, i: number) => (
                    <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" className="block p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:border-cyan-500 dark:hover:border-cyan-400 transition-colors">
                      <h4 className="text-lg font-semibold text-cyan-600 dark:text-cyan-400">{p.name}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{p.description}</p>
                      <span className="inline-block mt-2 text-xs text-cyan-600 dark:text-cyan-400">Open in new tab &rarr;</span>
                    </a>
                  ))}
                </div>
                {bookResult.disclaimer && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-4">{bookResult.disclaimer}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Disclaimer */}
      <div className="mt-8 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400">{t('common:disclaimer.travel')}</p>
      </div>
    </div>
  );
};

export default FlightRouteExplorer;
